<form [formGroup]="addTenantForm" (ngSubmit)="onSubmit()">
    <div class="dialog-header">
        <h2 class="dialog-title">Create New Tenant</h2>
        <button class="cancel-button" mat-button type="button" [disabled]="isSubmitting"
            (click)="onCancel()">Cancel</button>
    </div>
    <div class="dialog-body">
        <mat-dialog-content class="dialog-form">
            <div class="form-section">
                <h3 class="section-title">Tenant Information</h3>
                <mat-form-field>
                    <mat-label>Tenant Name</mat-label>
                    <input class="input" matInput formControlName="tenantName">
                    @if (addTenantForm.get('tenantName')?.errors?.['required']) {
                    <mat-error>Tenant Name is required.</mat-error>
                    }
                </mat-form-field>
                <mat-form-field>
                    <mat-label>Tenant Alias</mat-label>
                    <input class="input" matInput formControlName="tenantAlias">
                    @if (addTenantForm.get('tenantAlias')?.errors?.['required']) {
                    <mat-error>Tenant Alias is required.</mat-error>
                    }
                </mat-form-field>
                <div class="owner-account-container">
                    <mat-form-field class="owner-account-field">
                        <mat-label>
                            <mat-icon class="search-icon">search</mat-icon>
                            Owner Account
                        </mat-label>
                        <input class="input" type="text" placeholder="Search" matInput formControlName="selectedAccount"
                            [matAutocomplete]="auto"
                            (keyup.enter)="input.value.length >= 3 ? onSearch(input.value) : null" #input>
                        @if (input.value.length >= 3 && !loadingAccountSearch()) {
                        <div matSuffix>
                            <button class="remove-search-button" mat-icon-button type="button" (click)="removeSearch()">
                                <mat-icon>close</mat-icon>
                            </button>
                            <button class="search-button" mat-icon-button type="button" (click)="onSearch(input.value)">
                                <mat-icon>search</mat-icon>
                            </button>
                        </div>
                        }
                        @else if (loadingAccountSearch()) {
                        <div matSuffix class="account-loading"><app-loading-spinner
                                [strokeWidth]="3"></app-loading-spinner></div>
                        }

                        <mat-autocomplete requireSelection [displayWith]="displayAccount" #auto="matAutocomplete">
                            @if (accountList().length === 0 && searched) {
                            <mat-option disabled>No accounts found with your search.</mat-option>
                            } @else if (input.value.length < 3 && !searched) { <mat-option disabled>Enter 3 of more
                                characters to search</mat-option>
                                }
                                @for (account of accountList(); track account.userAccountId) {
                                <mat-option [value]="account">{{account.givenName}} {{account.surname}}:
                                    {{account.userAccountEmail}}</mat-option>
                                }
                        </mat-autocomplete>
                        @if (addTenantForm.get('selectedAccount')?.errors?.['required']) {
                        <mat-error>Owner account is required.</mat-error>
                        }
                    </mat-form-field>
                    <mat-icon matTooltip="Search accounts by email, first name or last name"
                        class="material-symbols-outlined info-icon">info_outline</mat-icon>
                    <button type="button" mat-flat-button class="use-account-button"
                        matTooltip="Use my account for the owner account" (click)="selectCurrentAccountAsOwner()">
                        @if (!loadingMyAccount()) {
                        Use My Account
                        } @else {
                        <div matSuffix class="account-loading"><app-loading-spinner
                                [strokeWidth]="3"></app-loading-spinner></div>
                        }
                    </button>
                </div>
                @if (accountErrorMessage) {
                <mat-error class="error-message">{{accountErrorMessage}}</mat-error>
                }
                <mat-form-field>
                    <mat-label>Marketplace Subscription ID</mat-label>
                    <input class="input" matInput formControlName="subscriptionId">
                    <mat-hint>Leave blank if not applicable.</mat-hint>
                </mat-form-field>
            </div>

            <mat-divider class="section-divider"></mat-divider>

            <div class="form-section">
                <h3 class="section-title">Data Collection</h3>
                <div class="feature-toggle-card">
                    <div class="feature-toggle-text">
                        <mat-icon class="material-symbols-outlined feature-toggle-icon">location_on</mat-icon>
                        <div class="feature-toggle-copy">
                            <p class="feature-toggle-title">Collect User Location</p>
                            <p class="feature-toggle-description">
                                Collection of user location data for analytics
                            </p>
                        </div>
                    </div>
                    <mat-slide-toggle formControlName="collectUserLocation"></mat-slide-toggle>
                </div>
            </div>

            <mat-divider class="section-divider"></mat-divider>

            <div class="form-section">
                <h3 class="section-title">Features</h3>
                <div class="feature-toggle-grid">
                    <div class="feature-toggle-card">
                        <div class="feature-toggle-text">
                            <mat-icon class="material-symbols-outlined feature-toggle-icon">dashboard</mat-icon>
                            <div class="feature-toggle-copy">
                                <p class="feature-toggle-title">Dashboards</p>
                                <p class="feature-toggle-description">Analytics dashboards and data visualisation tools
                                </p>
                            </div>
                        </div>
                        <mat-slide-toggle formControlName="hasDashboardsAccess"></mat-slide-toggle>
                    </div>
                    <div class="feature-toggle-card">
                        <div class="feature-toggle-text">
                            <mat-icon class="material-symbols-outlined feature-toggle-icon">slideshow</mat-icon>
                            <div class="feature-toggle-copy">
                                <p class="feature-toggle-title">Presentations</p>
                                <p class="feature-toggle-description">Presentation creation and sharing capabilities</p>
                            </div>
                        </div>
                        <mat-slide-toggle formControlName="hasPresentationsAccess"></mat-slide-toggle>
                    </div>
                    <div class="feature-toggle-card">
                        <div class="feature-toggle-text">
                            <mat-icon
                                class="material-symbols-outlined feature-toggle-icon">network_intelligence</mat-icon>
                            <div class="feature-toggle-copy">
                                <p class="feature-toggle-title">Cognition</p>
                                <p class="feature-toggle-description">
                                    AI-powered insights using custom agentic services
                                </p>
                            </div>
                        </div>
                        <mat-slide-toggle formControlName="hasCognitionAccess"></mat-slide-toggle>
                    </div>
                </div>
            </div>

            @if (submissionErrorMessage) {
            <mat-error class="error-message">{{ submissionErrorMessage }}</mat-error>
            }
            <div class="dialog-body-actions">
                @if (showCognitionWarning()) {
                <div class="cognition-warning">
                    <mat-icon class="material-symbols-outlined info-icon">info</mat-icon>
                    <span>Initialisation of Cognition may take up to a minute.</span>
                </div>
                }
                <button class="submit-button" mat-flat-button color="primary" type="submit"
                    [disabled]="addTenantForm.invalid || loadingFormSubmit()">
                    @if (!loadingFormSubmit()) {
                    Create Tenant
                    } @else {
                    <div class="loading-spinner"><app-loading-spinner [strokeWidth]="3"></app-loading-spinner></div>
                    }
                </button>
            </div>
        </mat-dialog-content>
    </div>
</form>